/**
 * Webhook `event_type` → human-readable RU label/explanation translator
 * для Business Demo skin (Issue #20).
 *
 * Source of truth для возможных `event_type` значений = `tools/canon-build`
 * + canonical webhook spec; перечислено 7 типов которые покрывают full-flow
 * happy-path + 2 чисто бизнес-significant failure cases (`AgreementDefaulted`,
 * `ContractCompleted`). Engineer-only события (`*Pending` для internal
 * orchestration) намеренно сворачиваются в один пользовательский label
 * «Обработка в реестре…» — не каждое тех-сообщение должно быть видно
 * бизнес-аудитории.
 *
 * Pattern observation (relevant to v2.36): translator живёт **только**
 * в frontend layer — backend webhook bridge всё ещё видит canonical
 * `eventType` строки. Этот файл = pure UI mapping, не contract change.
 * Если canonical eventType переименуется (что breaking), здесь нужно
 * будет добавить новый case + fallback на canonical-как-есть.
 */

export interface BusinessEventDisplay {
  /** Краткий заголовок в timeline-entry. */
  readonly title: string;
  /** 1-2 предложения объяснения для бизнес-пользователя. */
  readonly explanation: string;
  /** Иконка / эмодзи (Unicode, без зависимости на icon set). */
  readonly icon: string;
  /** Tone — controls badge / border colour. */
  readonly tone: 'success' | 'info' | 'warning' | 'danger';
}

const KNOWN_EVENTS: Record<string, BusinessEventDisplay> = {
  contractCreated: {
    title: 'Договор зарегистрирован',
    explanation:
      'Подписи обеих сторон проверены. Договор записан в распределённый реестр и доступен для проверки.',
    icon: '📝',
    tone: 'info',
  },
  agreementFinalized: {
    title: 'Договор подписан, средства переведены',
    explanation:
      'Обязательство по договору исполнено: средства переведены покупателем продавцу. Запись финализирована в реестре.',
    icon: '✅',
    tone: 'success',
  },
  agreementDefaulted: {
    title: 'Договор расторгнут — недостаточно средств',
    explanation:
      'Покупатель не смог исполнить обязательство в указанный срок (insufficient balance). Запись зафиксирована в реестре как «default».',
    icon: '⚠️',
    tone: 'warning',
  },
  contractCompleted: {
    title: 'Договор закрыт',
    explanation:
      'Все обязательства по сделке исполнены. Договор переведён в финальное состояние «закрыт».',
    icon: '🏁',
    tone: 'success',
  },
  tokensMinted: {
    title: 'Средства зачислены',
    explanation:
      'На счёт участника зачислены средства от эмитента (ISSUER) — стартовый баланс или восполнение.',
    icon: '💰',
    tone: 'info',
  },
  tokensBurned: {
    title: 'Средства списаны эмитентом',
    explanation:
      'Эмитент (ISSUER) списал часть средств со счёта участника — регуляторная или операционная корректировка.',
    icon: '🔥',
    tone: 'info',
  },
  actPending: {
    title: 'Этап исполнения зафиксирован',
    explanation:
      'Часть обязательства по композитному договору исполнена. Сумма резервируется в реестре до окончательного подтверждения.',
    icon: '🔄',
    tone: 'info',
  },
};

const FALLBACK_DISPLAY: BusinessEventDisplay = {
  title: 'Обработка в реестре…',
  explanation:
    'Системное событие реестра. Это техническое сообщение — для деталей переключитесь в режим разработчика.',
  icon: '⚙️',
  tone: 'info',
};

/**
 * Translate a canonical `event_type` (e.g. `agreementFinalized`) to a
 * business-friendly display object. Unknown / unparsed event_types fall
 * back to «Обработка в реестре…» — never throws, never returns null.
 */
export function translateEventType(eventType: string | null | undefined): BusinessEventDisplay {
  if (typeof eventType !== 'string' || eventType.length === 0) {
    return FALLBACK_DISPLAY;
  }
  return KNOWN_EVENTS[eventType] ?? FALLBACK_DISPLAY;
}

/**
 * Format a wire amount string (Gateway uses decimal strings, e.g. `"5000"`
 * or `"5000.00"`) в формат "5 000,00 ₽" для русскоязычной аудитории.
 * Non-numeric input passed through verbatim.
 */
export function formatAmount(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined) return '—';
  const asNumber = typeof raw === 'number' ? raw : Number.parseFloat(raw);
  if (!Number.isFinite(asNumber)) return String(raw);
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(asNumber);
}

/**
 * Format ISO timestamp в "1 июня 2026 г., 22:35" локали.
 * Returns the input verbatim if parsing fails.
 */
export function formatTimestamp(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.length === 0) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(d);
}
